import { valibotResolver } from "@hookform/resolvers/valibot";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { AppInput } from "~/components/AppInput";
import { Button } from "~/components/ui/button";
import { LoginForm, loginSchema } from "~/lib/schemas";
import { loginServer } from "~/lib/services/user";
import { cn } from "~/lib/utils";

export const Route = createFileRoute("/login")({
  component: Login,
});

function Login() {
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const methods = useForm<LoginForm>({
    resolver: valibotResolver(loginSchema),
  });

  const onSubmit = async (data: LoginForm) => {
    setIsLoading(true);

    try {
      const res = await loginServer({ data });

      if (res.error) {
        setError(res.error);
        return;
      }

      navigate({ to: "/" });
    } catch (e) {
      setError("Invalid email or password");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-dvh flex flex-col max-w-2xl mx-auto">
      <div className="pt-8 xl:pt-24">
        <img
          src="/images/login.png"
          alt="Tory Bike Drivers"
          className="w-full object-cover"
        />
      </div>

      <div className="flex-1 p-6 flex flex-col">
        <div className="mb-8">
          <h1 className="text-5xl font-bold text-black">Welcome back</h1>
          <p className="text-lg mt-2">Enter your details to continue</p>
        </div>

        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(onSubmit)} className="space-y-6">
            <AppInput name="email" type="email" label="Email" />

            <div className="space-y-2">
              <div className="relative">
                <AppInput
                  name="password"
                  type={showPassword ? "text" : "password"}
                  label="Password"
                  className={cn("")}
                />
                <Button
                  type="button"
                  className="absolute right-2 top-[65%] -translate-y-1/2 text-sm text-foreground shadow-none bg-transparent hover:bg-transparent underline"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? "Hide" : "Show"}
                </Button>
              </div>
              <div className="w-full grid justify-end">
                <Link
                  className="w-full text-sm underline font-semibold"
                  to="/forgot-password"
                >
                  Forgot password?
                </Link>
              </div>
            </div>

            <Button isLoading={isLoading} type="submit" className="w-full">
              Log in
            </Button>
          </form>
        </FormProvider>
        {error && (
          <p className="text-destructive text-sm text-center py-4">{error}</p>
        )}
      </div>
    </div>
  );
}
